import { defineStore } from 'pinia';
import { LocalStorage } from 'shared/helpers/localStorage';
import { stripAttachedContext } from 'shared/helpers/copilotMentionContext';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const SORT_OPTIONS = [
  'updated_desc',
  'updated_asc',
  'created_desc',
  'created_asc',
];
const GROUP_OPTIONS = ['date', 'assistant', 'none'];
const ASSISTANT_ORDER = [
  'copilot',
  'seo',
  'sales',
  'events',
  'revops',
  'insights',
];

function loadPrefs() {
  const prefs = LocalStorage.get(LOCAL_STORAGE_KEYS.COPILOT_THREAD_PREFS) || {};
  return {
    sortBy: SORT_OPTIONS.includes(prefs.sortBy) ? prefs.sortBy : 'updated_desc',
    groupBy: GROUP_OPTIONS.includes(prefs.groupBy) ? prefs.groupBy : 'date',
  };
}

function savePrefs({ sortBy, groupBy }) {
  LocalStorage.set(LOCAL_STORAGE_KEYS.COPILOT_THREAD_PREFS, {
    sortBy,
    groupBy,
  });
}

function getAssistantId(thread) {
  return (
    thread.metadata?.graph_id || thread.metadata?.assistant_id || 'copilot'
  );
}

export function deriveThreadTitle(content, maxLen = 60) {
  let text = '';
  if (typeof content === 'string') {
    text = content;
  } else if (Array.isArray(content)) {
    text = content
      .filter(block => block?.type === 'text' && block.text)
      .map(block => block.text)
      .join(' ');
  } else if (content?.[0]?.text) {
    text = content[0].text;
  }

  text = stripAttachedContext(text).replace(/\s+/g, ' ').trim();

  if (!text) return '';
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1).trim()}…`;
}

export function getThreadTitle(thread, emptyLabel = '') {
  const stored = thread?.metadata?.title?.trim();
  if (stored) return stored;

  const messages = thread?.values?.messages || [];
  const firstHuman = messages.find(m => m.type === 'human');
  if (!firstHuman) return emptyLabel;

  return deriveThreadTitle(firstHuman.content) || emptyLabel;
}

function getDateGroupKey(isoString) {
  if (!isoString) return 'older';
  const date = new Date(isoString);
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const diffDays = Math.round((startOfToday - startOfDate) / 86400000);

  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return 'previous_7_days';
  return 'older';
}

function sortThreads(threads, sortBy) {
  const [field, direction] = sortBy.split('_');
  const key = field === 'created' ? 'created_at' : 'updated_at';
  const factor = direction === 'asc' ? 1 : -1;

  return [...threads].sort((a, b) => {
    const aTime = new Date(a[key] || a.updated_at || 0).getTime();
    const bTime = new Date(b[key] || b.updated_at || 0).getTime();
    return (aTime - bTime) * factor;
  });
}

function groupByDate(threads) {
  const order = ['today', 'yesterday', 'previous_7_days', 'older'];
  const buckets = Object.fromEntries(order.map(key => [key, []]));

  threads.forEach(thread => {
    const key = getDateGroupKey(thread.updated_at || thread.created_at);
    buckets[key].push(thread);
  });

  return order
    .filter(key => buckets[key].length > 0)
    .map(key => ({ key, type: 'date', threads: buckets[key] }));
}

function groupByAssistant(threads) {
  const buckets = {};

  threads.forEach(thread => {
    const key = getAssistantId(thread);
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(thread);
  });

  const keys = Object.keys(buckets).sort((a, b) => {
    const aIdx = ASSISTANT_ORDER.indexOf(a);
    const bIdx = ASSISTANT_ORDER.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  return keys.map(key => ({
    key,
    type: 'assistant',
    threads: buckets[key],
  }));
}

const initialPrefs = loadPrefs();

export const useCopilotStore = defineStore('copilot', {
  state: () => ({
    threads: [],
    threadsLoading: false,
    activeThreadId: null,
    sidebarOpen: false,
    searchQuery: '',
    sortBy: initialPrefs.sortBy,
    groupBy: initialPrefs.groupBy,
  }),

  getters: {
    visibleThreads: state =>
      state.threads.filter(
        t => t.values?.messages?.length > 0 && !t.metadata?.archived
      ),

    sortedThreads() {
      return sortThreads(this.visibleThreads, this.sortBy);
    },

    filteredThreads() {
      const sorted = this.sortedThreads;
      if (!this.searchQuery.trim()) return sorted;
      const q = this.searchQuery.toLowerCase();
      return sorted.filter(t => {
        const title = getThreadTitle(t).toLowerCase();
        if (title.includes(q)) return true;
        const msgs = t.values?.messages || [];
        return msgs.some(m => {
          const text =
            typeof m.content === 'string'
              ? m.content
              : m.content?.[0]?.text || '';
          return text.toLowerCase().includes(q);
        });
      });
    },

    groupedThreads() {
      const threads = this.filteredThreads;
      if (this.groupBy === 'assistant') return groupByAssistant(threads);
      if (this.groupBy === 'none') {
        return threads.length ? [{ key: 'all', type: 'none', threads }] : [];
      }
      return groupByDate(threads);
    },
  },

  actions: {
    setSortBy(sortBy) {
      if (!SORT_OPTIONS.includes(sortBy)) return;
      this.sortBy = sortBy;
      savePrefs({ sortBy: this.sortBy, groupBy: this.groupBy });
    },

    setGroupBy(groupBy) {
      if (!GROUP_OPTIONS.includes(groupBy)) return;
      this.groupBy = groupBy;
      savePrefs({ sortBy: this.sortBy, groupBy: this.groupBy });
    },

    async fetchThreads(accountId, { apiUrl, getToken } = {}) {
      if (!apiUrl || !getToken) return;

      this.threadsLoading = true;
      try {
        const token = await getToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${apiUrl.replace(/\/$/, '')}/threads/search`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            limit: 50,
            sort_by: 'updated_at',
            sort_order: 'desc',
          }),
        });
        if (res.ok) {
          this.threads = await res.json();
        }
      } finally {
        this.threadsLoading = false;
      }
    },

    upsertThread(threadId, { metadata = {}, assistantId, values } = {}) {
      if (!threadId) return;

      const now = new Date().toISOString();
      const existing = this.threads.find(t => t.thread_id === threadId);
      const nextMetadata = {
        ...(existing?.metadata || {}),
        ...metadata,
      };
      if (assistantId) {
        nextMetadata.graph_id = assistantId;
      }

      if (existing) {
        this.threads = this.threads.map(t =>
          t.thread_id === threadId
            ? {
                ...t,
                metadata: nextMetadata,
                updated_at: now,
                values: values || t.values,
              }
            : t
        );
        return;
      }

      this.threads = [
        {
          thread_id: threadId,
          metadata: nextMetadata,
          created_at: now,
          updated_at: now,
          values: values || { messages: [{ type: 'human', content: '' }] },
        },
        ...this.threads,
      ];
    },

    async renameThread(threadId, title, { apiUrl, getToken } = {}) {
      if (!apiUrl || !getToken || !threadId) return false;

      const nextTitle = String(title || '').trim();
      if (!nextTitle) return false;

      const token = await getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${apiUrl.replace(/\/$/, '')}/threads/${threadId}`,
        {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify({ metadata: { title: nextTitle } }),
        }
      );
      if (!res.ok) return false;

      this.threads = this.threads.map(t =>
        t.thread_id === threadId
          ? { ...t, metadata: { ...t.metadata, title: nextTitle } }
          : t
      );
      return true;
    },

    async archiveThread(threadId, { apiUrl, getToken } = {}) {
      if (!apiUrl || !getToken || !threadId) return false;

      const token = await getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${apiUrl.replace(/\/$/, '')}/threads/${threadId}`,
        {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify({ metadata: { archived: true } }),
        }
      );
      if (!res.ok) return false;

      this.threads = this.threads.map(t =>
        t.thread_id === threadId
          ? { ...t, metadata: { ...t.metadata, archived: true } }
          : t
      );
      if (this.activeThreadId === threadId) {
        this.activeThreadId = null;
      }
      return true;
    },

    async deleteThread(threadId, { apiUrl, getToken } = {}) {
      if (!apiUrl || !getToken || !threadId) return false;

      const token = await getToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${apiUrl.replace(/\/$/, '')}/threads/${threadId}`,
        {
          method: 'DELETE',
          headers,
          credentials: 'include',
        }
      );
      if (!res.ok) return false;

      this.threads = this.threads.filter(t => t.thread_id !== threadId);
      if (this.activeThreadId === threadId) {
        this.activeThreadId = null;
      }
      return true;
    },

    setActiveThread(threadId) {
      this.activeThreadId = threadId;
    },

    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
      if (!this.sidebarOpen) {
        this.searchQuery = '';
      }
    },

    closeSidebar() {
      this.sidebarOpen = false;
      this.searchQuery = '';
    },
  },
});
